import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { IntegrationPicker } from "../../components/admin/IntegrationPicker";
import { Logo } from "../../components/dashboard/Logo";
import { KebabMenu } from "../../components/KebabMenu";
import { ModalShell } from "../../components/ModalShell";
import { Toast, toneForMessage, useToast } from "../../components/Toast";
import { type AdminInvite, useAdminInvites } from "../../hooks/useAdminInvites";
import {
  isAppGroupToken,
  useAppChoiceGroups,
  useIntegrationsBySlug,
} from "../../hooks/useIntegrationsCatalog";
import { isValidEmail } from "../../utils/email";
import { formatDateTime, formatRelativeTime } from "../../utils/formatDateTime";
import styles from "../AdminInstances.module.css";
import flagStyles from "./AdminFeatureFlags.module.css";
import local from "./AdminInvites.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

type Stage = "invited" | "signed-in" | "launched" | "revoked";

// One invite has exactly one stage. "Launched" is the terminal one — the
// invite did its job and the customer owns an OpenClaw — so those rows drop
// out of the default view along with revoked ones.
const stageOf = (invite: AdminInvite): Stage => {
  if (invite.status === "REVOKED") return "revoked";
  if (invite.instanceCount > 0) return "launched";
  return invite.status === "ACCEPTED" ? "signed-in" : "invited";
};

const STAGE_LABEL: Record<Stage, string> = {
  invited: "Invited",
  "signed-in": "Signed in",
  launched: "Launched",
  revoked: "Revoked",
};

// Shared pills for the ends of the funnel; a local one for the middle
// (see AdminInvites.module.css).
const pillClass = (stage: Stage): string => {
  switch (stage) {
    case "invited": return styles.pill_INVITED ?? "";
    case "signed-in": return local.pillSignedIn ?? "";
    case "launched": return styles.pill_ACTIVE ?? "";
    case "revoked": return styles.pill_REVOKED ?? "";
  }
};

const isLive = (invite: AdminInvite): boolean => {
  const stage = stageOf(invite);
  return stage === "invited" || stage === "signed-in";
};

export const AdminInvites = (): JSX.Element => {
  const {
    status, invites, errorMessage, isFetching,
    createInvite, setIntegrations, resendInvite, revokeInvite,
  } = useAdminInvites();

  const { toast, showToast, dismissToast } = useToast();
  const setFlash = useCallback((message: string) => {
    showToast(message, toneForMessage(message));
  }, [showToast]);

  const [showAll, setShowAll] = useState<boolean>(false);
  const [inviteOpen, setInviteOpen] = useState<boolean>(false);
  const [editingApps, setEditingApps] = useState<AdminInvite | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const rows = useMemo(
    () => (showAll ? invites : invites.filter(isLive)),
    [invites, showAll],
  );
  const liveCount = useMemo(() => invites.filter(isLive).length, [invites]);

  // One lookup for every slug on screen, so a table of ten invites costs one
  // catalog request rather than one per row.
  const allSlugs = useMemo(
    () => [...new Set(rows.flatMap((i) => i.requestedIntegrations.filter((t) => !isAppGroupToken(t))))],
    [rows],
  );
  const { bySlug } = useIntegrationsBySlug(allSlugs);
  // Choice groups ("Email") are pinned as tokens, not slugs, so they resolve
  // through their own lookup rather than the catalog.
  const { groups } = useAppChoiceGroups();
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const handleResend = async (email: string): Promise<void> => {
    setBusy(email);
    const result = await resendInvite(email);
    setBusy(null);
    setFlash(result.ok ? `Re-sent the invite to ${email}.` : `Resend failed: ${result.message}`);
  };

  const handleRevoke = async (email: string): Promise<void> => {
    setBusy(email);
    const result = await revokeInvite(email);
    setBusy(null);
    setFlash(result.ok ? `Revoked the invite for ${email}.` : `Revoke failed: ${result.message}`);
  };

  const handleSaveApps = async (email: string, slugs: ReadonlyArray<string>): Promise<boolean> => {
    const result = await setIntegrations(email, slugs);
    if (result.ok) {
      setFlash(
        slugs.length === 0
          ? `Cleared the pinned apps for ${email}.`
          : `${email} will be asked to connect ${slugs.length} app${slugs.length === 1 ? "" : "s"}.`,
      );
      setEditingApps(null);
      return true;
    }
    setFlash(`Save failed: ${result.message}`);
    return false;
  };

  const renderApps = (invite: AdminInvite): JSX.Element => (
    <button
      type="button"
      className={local.appsCell}
      onClick={() => setEditingApps(invite)}
      disabled={invite.status === "REVOKED"}
      aria-label={`Edit pinned apps for ${invite.email}`}
    >
      {invite.requestedIntegrations.length === 0 ? (
        <span className={local.addApps}>+ Add apps</span>
      ) : (
        invite.requestedIntegrations.map((token) => {
          const group = groupById.get(token);
          if (group !== undefined) {
            return (
              <span key={token} className={local.appChip} data-testid="invite-app-chip">
                <span className={local.groupMark} aria-hidden="true">✉</span>
                {group.title}
                <span className={local.groupHint}>any</span>
              </span>
            );
          }
          const entry = bySlug.get(token);
          return (
            <span key={token} className={local.appChip} data-testid="invite-app-chip">
              <Logo
                slug={token}
                size={14}
                brandHex={entry?.iconBrandHex ?? null}
                logoUrl={entry?.ourLogoUrl ?? entry?.logoUrl ?? null}
              />
              {entry?.title ?? token}
            </span>
          );
        })
      )}
    </button>
  );

  const renderRow = (invite: AdminInvite): JSX.Element => {
    const stage = stageOf(invite);
    return (
      <tr key={invite.email}>
        <td className={styles.owner}>
          <span className={styles.ownerEmail} title={invite.email}>
            {invite.email}
          </span>
        </td>
        <td className={local.stageCell}>
          <span className={`${styles.pill} ${pillClass(stage)}`}>{STAGE_LABEL[stage]}</span>
        </td>
        <td>{renderApps(invite)}</td>
        <td className={`${styles.muted} ${local.whenCell}`}>
          <span title={formatDateTime(invite.invitedAt)}>{formatRelativeTime(invite.invitedAt)}</span>
        </td>
        <td className={`${styles.muted} ${styles.nowrap}`}>{invite.invitedBy}</td>
        <td className={styles.actionCell}>
          <div className={styles.actionCellInner}>
            {busy === invite.email && <span className={styles.actionStatus}>Working…</span>}
            <KebabMenu
              ariaLabel={`Actions for ${invite.email}`}
              items={[
                {
                  label: "Edit apps",
                  onClick: () => setEditingApps(invite),
                  disabled: invite.status === "REVOKED",
                  ...(invite.status === "REVOKED"
                    ? { disabledReason: "Revoked — send a fresh invite first" }
                    : {}),
                },
                {
                  label: "Resend invite",
                  onClick: () => void handleResend(invite.email),
                  disabled: invite.status !== "PENDING",
                  ...(invite.status !== "PENDING"
                    ? { disabledReason: "Only an unclaimed invite can be re-sent" }
                    : {}),
                },
                {
                  label: "Revoke invite",
                  destructive: true,
                  onClick: () => void handleRevoke(invite.email),
                  disabled: invite.status !== "PENDING",
                  ...(invite.status !== "PENDING"
                    ? { disabledReason: "Already claimed — delete the user from the Users tab" }
                    : {}),
                },
              ]}
            />
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Invites">
        Invites that haven&apos;t become an OpenClaw yet. Pinned apps are offered at the end of onboarding, with a link to each setup page.
      </AdminPageIntro>

      <div className={styles.toolbar}>
        <div className={styles.toolbarMeta}>
          {status === "ready" ? `${liveCount} live · ${invites.length} total` : "Loading…"}
        </div>
        <div className={styles.toolbarActions}>
          <label className={local.filterToggle}>
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            Show launched + revoked
          </label>
          <button type="button" className={styles.primaryBtn} onClick={() => setInviteOpen(true)}>
            Invite user
          </button>
        </div>
      </div>

      <section className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Email", "Status", "Apps to connect", "Invited", "Invited by", ""]}
            tableClassName={styles.table ?? ""}
          />
        )}
        {status === "error" && <div className={styles.empty}>{errorMessage ?? "Failed to load."}</div>}
        {status === "ready" && rows.length === 0 && (
          <div className={styles.empty}>
            {invites.length === 0
              ? "No invites yet — invite a customer to get started."
              : "No live invites. Every invite has been claimed or revoked."}
          </div>
        )}
        {status === "ready" && rows.length > 0 && (
          <table className={isFetching ? `${styles.table} ${local.stale}` : styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Apps to connect</th>
                <th>Invited</th>
                <th>Invited by</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>{rows.map(renderRow)}</tbody>
          </table>
        )}
      </section>

      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onSend={async (email, slugs) => {
            const result = await createInvite(email, slugs);
            if (result.ok) {
              setFlash(`Invited ${email}.`);
              setInviteOpen(false);
              return { ok: true, message: "" };
            }
            return { ok: false, message: result.message };
          }}
        />
      )}

      {editingApps && (
        <EditAppsDialog
          invite={editingApps}
          onClose={() => setEditingApps(null)}
          onSave={(slugs) => handleSaveApps(editingApps.email, slugs)}
        />
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

type InviteDialogProps = {
  onClose: () => void;
  onSend: (email: string, slugs: ReadonlyArray<string>) => Promise<{ ok: boolean; message: string }>;
};

const InviteDialog = ({ onClose, onSend }: InviteDialogProps): JSX.Element => {
  const [email, setEmail] = useState<string>("");
  const [slugs, setSlugs] = useState<ReadonlyArray<string>>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const validEmail = isValidEmail(email);
  const titleId = "invite-dialog-title";

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId}>
      <header className={flagStyles.dialogHeader}>
        <h3 className={flagStyles.dialogTitle} id={titleId}>Invite user</h3>
        <button type="button" className={flagStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!validEmail) {
            setError("Enter a valid email address.");
            return;
          }
          setSubmitting(true);
          setError(null);
          const result = await onSend(email.trim().toLowerCase(), slugs);
          setSubmitting(false);
          if (!result.ok) setError(result.message);
        }}
      >
        <label className={flagStyles.field}>
          <span>Customer email</span>
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            required
            disabled={submitting}
          />
        </label>

        <div className={local.dialogApps}>
          <p className={flagStyles.hint}>Apps to connect (optional)</p>
          <IntegrationPicker selected={slugs} onChange={setSlugs} disabled={submitting} />
        </div>

        {error && <p className={flagStyles.errorBanner}>{error}</p>}

        <footer className={flagStyles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={submitting || !validEmail}
            title={!validEmail ? "Enter a valid email" : undefined}
          >
            {submitting ? "Sending…" : "Send invite"}
          </button>
        </footer>
      </form>
    </ModalShell>
  );
};

type EditAppsDialogProps = {
  invite: AdminInvite;
  onClose: () => void;
  onSave: (slugs: ReadonlyArray<string>) => Promise<boolean>;
};

const EditAppsDialog = ({ invite, onClose, onSave }: EditAppsDialogProps): JSX.Element => {
  const [slugs, setSlugs] = useState<ReadonlyArray<string>>([...invite.requestedIntegrations]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const titleId = `invite-apps-dialog-${invite.email}`;

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId}>
      <header className={flagStyles.dialogHeader}>
        <h3 className={flagStyles.dialogTitle} id={titleId}>
          Apps to connect — <code>{invite.email}</code>
        </h3>
        <button type="button" className={flagStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setSubmitting(true);
          const saved = await onSave(slugs);
          if (!saved) setSubmitting(false);
        }}
      >
        <IntegrationPicker selected={slugs} onChange={setSlugs} disabled={submitting} />

        <footer className={flagStyles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={submitting}>
            {submitting ? "Saving…" : "Save apps"}
          </button>
        </footer>
      </form>
    </ModalShell>
  );
};
